'use client';

import { Open } from '@drift-labs/icons';
import { cj } from 'src/utils/classNames';
import NumberDisplay from '../../../NumberDisplay/NumberDisplay';
import InlineLoadingBar from '../../../InlineLoadingBar/InlineLoadingBar';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import TableV2 from '../../../Tables/TableV2';
import { getUrlForAccount, getUrlForTx } from 'src/utils/explorer';
import { useOrderRowData } from './useOrderRowData';
import type { OrderRowCollapsedContentProps } from './types';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';

export default function OrderRowCollapsedContentDesktop({
	orderId,
	displayDatesAsTimestamp,
	expanded,
	amountDisplayType,
	orderedQuoteValue,
}: OrderRowCollapsedContentProps) {
	const { processedRecords, isLoading, hasData } = useOrderRowData({
		orderId,
		expanded,
	});

	const devSwitchIsOn = useDevSwitchIsOn();

	if (!hasData) {
		if (isLoading) {
			return (
				<div className="flex h-full w-full items-center justify-center py-4">
					<InlineLoadingBar />
				</div>
			);
		}
		return null;
	}

	return (
		<div className="w-full rounded-sm border border-stroke-divider bg-container-bg p-md">
			{processedRecords.map((data, index) => (
				<div
					key={`${data.record.txSig}_${index}`}
					className={cj(
						'flex w-full justify-between bg-container-bg',
						index !== processedRecords.length - 1 &&
							'border-b border-stroke-divider pb-lg',
						index !== 0 && 'pt-lg'
					)}
				>
					<div className="flex w-[18%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">Time</div>
						<TableV2.DateCell
							className="py-0 typo-t4 text-text-default"
							displayAsTimestamp={displayDatesAsTimestamp}
							date={data.date}
						/>
					</div>

					<div className="flex w-[12%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">Avg. Fill Price</div>
						<div className="typo-t4 text-text-default">
							{data.isNonFillAction ? '-' : data.avgPriceBn.toNotional(true)}
						</div>
					</div>

					<div className="flex w-[10%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">
							{amountDisplayType === 'base' ? 'Order Size' : 'Order Value'}
						</div>
						<div className="typo-t4 text-text-default">
							{data.orderedIsEntire ? (
								'Entire Position'
							) : amountDisplayType === 'base' ? (
								<NumberDisplay
									value={data.tradeInfo.baseAssetAmount}
									type="number"
									precision="trade"
									isAccountValueToHide
									trimZeroes
								/>
							) : (
								(orderedQuoteValue ?? 'Market')
							)}
						</div>
					</div>

					<div className="flex w-[10%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">
							{amountDisplayType === 'base' ? 'Filled Size' : 'Filled Value'}
						</div>
						<div className="typo-t4 text-text-default">
							{data.isNonFillAction ? (
								'-'
							) : amountDisplayType === 'base' ? (
								<NumberDisplay
									value={data.tradeInfo.baseAssetAmountFilled}
									type="number"
									precision="trade"
									isAccountValueToHide
									trimZeroes
								/>
							) : (
								data.tradeInfo.quoteAssetAmountFilled.toNotional()
							)}
						</div>
					</div>

					<div className="flex w-[15%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">
							{amountDisplayType === 'base'
								? 'Cumulative Size'
								: 'Cumulative Value'}
						</div>
						<div className="typo-t4 text-text-default">
							{data.isNonFillAction ? (
								'-'
							) : amountDisplayType === 'base' ? (
								<NumberDisplay
									value={data.tradeInfo.totalBaseAmountFilled}
									type="number"
									precision="trade"
									isAccountValueToHide
									trimZeroes
								/>
							) : (
								data.tradeInfo.totalQuoteAmountFilled.toNotional()
							)}
						</div>
					</div>

					<div className="flex w-[10%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">Fee</div>
						<div className="typo-t4 text-text-default">
							{data.isNonFillAction || !data.tradeInfo.fee ? (
								'-'
							) : (
								<NumberDisplay value={data.tradeInfo.fee} type="currency" />
							)}
						</div>
					</div>

					<div className="flex w-[12%] flex-col gap-y-xs">
						<div className="typo-b4 text-text-secondary">Filled by</div>
						<div className="typo-t4 text-text-default">
							{data.isNonFillAction ? (
								'-'
							) : data.isDefaultCounterparty ? (
								data.filledBy
							) : (
								<a
									href={
										data.counterpartyStr
											? getUrlForAccount(data.counterpartyStr)
											: '#'
									}
									target="_blank"
									rel="noopener noreferrer"
								>
									{data.filledBy}
								</a>
							)}
						</div>
					</div>

					<div className="flex w-[13%] flex-col items-end gap-y-xs">
						<div className="typo-b4 text-text-secondary">Status</div>
						<div className="relative">
							<div className="flex items-center gap-1 typo-t4 whitespace-nowrap text-text-default">
								{data.status === 'Partially Filled (Cancelled)'
									? 'Cancelled'
									: data.status}
								{data.record.txSig && (
									<a
										href={getUrlForTx(data.record.txSig)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex"
									>
										<Open autoSize />
									</a>
								)}
							</div>
							{devSwitchIsOn && data.cancelExplanation && (
								<div className="absolute -top-3 -right-2 flex items-center justify-end gap-1">
									<Tooltip
										content={
											<>
												<p>this is devmode</p>
												<p>{data.cancelExplanation}</p>
											</>
										}
									>
										<div className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-input-bg text-[10px] leading-none text-text-secondary">
											!
										</div>
									</Tooltip>
								</div>
							)}
						</div>
					</div>
				</div>
			))}
		</div>
	);
}
